import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["horizontalAlignment", "marginAroundElement", "verticalAlignment"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Portal } from "../../portal";
import { useEventListener } from "../../utilities";
import { TOOLTIP_HORIZONTAL_ALIGNMENTS, TOOLTIP_VERTICAL_ALIGNMENTS } from "../constants";
import { Tooltip } from "../elements";
import { tooltipPositioningDefaultProps, tooltipPositioningProps } from "../propTypes";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var windowLeftOffset = 0;
var windowTopOffset = 0;

var withTooltip = function withTooltip(Component, tooltipProps) {
  var WithTooltip = /*#__PURE__*/forwardRef(function (_ref, ref) {
    var horizontalAlignment = _ref.horizontalAlignment,
        marginAroundElement = _ref.marginAroundElement,
        verticalAlignment = _ref.verticalAlignment,
        other = _objectWithoutProperties(_ref, _excluded);

    var id = tooltipProps.id,
        styles = tooltipProps.styles,
        text = tooltipProps.text;
    var tooltipStyling = styles || {};
    var isWindow = window !== 'undefined';
    var isDocumentReady = document !== 'undefined';
    var isInitialVerticalPositionMiddle = verticalAlignment === TOOLTIP_VERTICAL_ALIGNMENTS.MIDDLE;
    var isInitialHorizontalPositionMiddle = horizontalAlignment === TOOLTIP_HORIZONTAL_ALIGNMENTS.MIDDLE;
    var componentContainerRef = useRef();
    var tooltipRef = useRef();

    var _useState = useState(false),
        _useState2 = _slicedToArray(_useState, 2),
        isVisible = _useState2[0],
        setIsVisible = _useState2[1];

    var _useState3 = useState(true),
        _useState4 = _slicedToArray(_useState3, 2),
        showTooltip = _useState4[0],
        setShowTooltip = _useState4[1];

    var _useState5 = useState(text),
        _useState6 = _slicedToArray(_useState5, 2),
        tooltipText = _useState6[0],
        setTooltipText = _useState6[1];

    var _useState7 = useState({}),
        _useState8 = _slicedToArray(_useState7, 2),
        windowScrollOffset = _useState8[0],
        setWindowScrollOffset = _useState8[1];

    var _useState9 = useState({
      left: 0
    }),
        _useState10 = _slicedToArray(_useState9, 2),
        tooltipHorizontalOffset = _useState10[0],
        setTooltipHorizontalOffset = _useState10[1];

    var _useState11 = useState({
      top: 0
    }),
        _useState12 = _slicedToArray(_useState11, 2),
        tooltipVerticalOffset = _useState12[0],
        setTooltipVerticalOffset = _useState12[1];

    var documentSize = isDocumentReady && document.documentElement;
    var documentHeight = documentSize.clientHeight;
    var documentWidth = documentSize.clientWidth;
    var getPositionValue = useCallback(function (value) {
      return isVisible ? value : 0;
    }, [isVisible]);
    var setFullWidthTooltip = useCallback(function () {
      return setTooltipHorizontalOffset({
        left: getPositionValue(marginAroundElement),
        right: getPositionValue(marginAroundElement)
      });
    }, [getPositionValue, marginAroundElement]);
    useEventListener('scroll', function () {
      if (isWindow) {
        setWindowScrollOffset({
          x: window.pageXOffset,
          y: window.pageYOffset
        });
      }
    });
    useEffect(function () {
      // Component dimensions
      var componentDimensions = !!componentContainerRef.current && componentContainerRef.current.getBoundingClientRect();
      var componentBottom = componentDimensions.bottom;
      var componentHeight = componentDimensions.height;
      var componentLeft = componentDimensions.left;
      var componentTop = componentDimensions.top;
      var componentRight = componentDimensions.right;
      var componentWidth = componentDimensions.width;
      var halfComponentHeight = componentHeight / 2; // Tooltip dimensions

      var tooltipDimensions = !!tooltipRef.current && tooltipRef.current.getBoundingClientRect();
      var tooltipHeight = tooltipDimensions.height;
      var tooltipTop = tooltipDimensions.top;
      var tooltipWidth = tooltipDimensions.width;
      var halfTooltipHeight = tooltipHeight / 2; // Intersections
      // Bottom

      var isTooltipIntersectingWindowBottom = documentHeight < componentBottom + marginAroundElement + tooltipHeight; // Top

      var isTooltipFitableBetweenComponentAndWindowTop = tooltipHeight < componentTop;
      var isTooltipIntersectingWindowTop = tooltipTop < windowTopOffset;
      var canTooltipBeDisplayedTop = !isTooltipIntersectingWindowTop && isTooltipFitableBetweenComponentAndWindowTop;
      var isMiddleVerticalTooltipIntersectingWindowTop = halfTooltipHeight - halfComponentHeight > componentTop;
      var isMiddleVerticalTooltipIntersectingWindowBottom = halfTooltipHeight - halfComponentHeight > documentHeight - componentBottom; // Left

      var isTooltipIntersectingWindowLeft = componentRight - marginAroundElement - tooltipWidth < windowLeftOffset;
      var isTooltipFitableBetweenComponentAndWindowLeft = tooltipWidth + marginAroundElement < componentLeft; // Right

      var isTooltipIntersectingWindowRight = componentLeft + tooltipWidth + marginAroundElement > documentWidth;
      var isTooltipFitableBetweenComponentAndWindowRight = tooltipWidth + marginAroundElement < documentWidth - componentRight; // Tooltip alignments
      // Vertical

      var alignBottom = "".concat(componentBottom + marginAroundElement, "px");
      var alignTop = "".concat(componentTop - tooltipHeight - marginAroundElement, "px");
      var alignMiddleVertical = "".concat(componentTop - tooltipHeight / 2 + componentHeight / 2, "px"); // Horizontal

      var alignLeft = "".concat(componentLeft, "px");
      var alignMiddleVerticalLeftHorizontal = "".concat(componentLeft - tooltipWidth - marginAroundElement, "px");
      var alignRight = "".concat(documentWidth - componentRight, "px"); // Must be set as right: alignRight

      var alignMiddleVerticalRightHorizontal = "".concat(componentRight + marginAroundElement, "px");
      var alignMiddleHorizonal = "".concat(componentLeft - tooltipWidth / 2 + componentWidth / 2, "px"); // Position handlers
      // Vertical

      var handlePositionTop = function handlePositionTop() {
        if (canTooltipBeDisplayedTop) {
          setTooltipVerticalOffset({
            top: getPositionValue(alignTop)
          });
        } else {
          setTooltipVerticalOffset({
            top: getPositionValue(alignBottom)
          });
        }
      };

      var handlePositionBottom = function handlePositionBottom() {
        if (!isTooltipIntersectingWindowBottom) {
          setTooltipVerticalOffset({
            top: getPositionValue(alignBottom)
          });
        } else {
          setTooltipVerticalOffset({
            top: getPositionValue(alignTop)
          });
        }
      };

      var handlePositionMiddle = function handlePositionMiddle() {
        if (isMiddleVerticalTooltipIntersectingWindowTop) {
          setTooltipVerticalOffset({
            top: getPositionValue(alignBottom)
          });
        } else if (isMiddleVerticalTooltipIntersectingWindowBottom) {
          setTooltipVerticalOffset({
            top: getPositionValue(alignTop)
          });
        } else if (!isInitialHorizontalPositionMiddle && (isTooltipFitableBetweenComponentAndWindowLeft || isTooltipFitableBetweenComponentAndWindowRight)) {
          setTooltipVerticalOffset({
            top: getPositionValue(alignMiddleVertical)
          });
        } else {
          handlePositionTop();
        }
      }; // Horizontal


      var handlePositionLeft = function handlePositionLeft() {
        if (!isTooltipIntersectingWindowRight) {
          setTooltipHorizontalOffset({
            left: getPositionValue(alignLeft)
          });
        } else if (isTooltipIntersectingWindowRight && !isTooltipIntersectingWindowLeft) {
          setTooltipHorizontalOffset({
            right: getPositionValue(alignRight)
          });
        } else {
          setFullWidthTooltip();
        }
      };

      var handlePositionRight = function handlePositionRight() {
        if (!isTooltipIntersectingWindowLeft) {
          setTooltipHorizontalOffset({
            right: getPositionValue(alignRight)
          });
        } else if (isTooltipIntersectingWindowLeft && !isTooltipIntersectingWindowRight) {
          setTooltipHorizontalOffset({
            left: getPositionValue(alignLeft)
          });
        } else {
          setFullWidthTooltip();
        }
      };

      var handlePositionMiddleVerticalLeftHorizontal = function handlePositionMiddleVerticalLeftHorizontal() {
        if (isTooltipFitableBetweenComponentAndWindowLeft && !isMiddleVerticalTooltipIntersectingWindowTop && !isMiddleVerticalTooltipIntersectingWindowBottom) {
          setTooltipHorizontalOffset({
            left: getPositionValue(alignMiddleVerticalLeftHorizontal)
          });
        } else if (isTooltipFitableBetweenComponentAndWindowRight && !isMiddleVerticalTooltipIntersectingWindowTop && !isMiddleVerticalTooltipIntersectingWindowBottom) {
          setTooltipHorizontalOffset({
            left: getPositionValue(alignMiddleVerticalRightHorizontal)
          });
        } else if (!isTooltipIntersectingWindowLeft) {
          setTooltipHorizontalOffset({
            right: getPositionValue(alignRight)
          });
        } else if (!isTooltipIntersectingWindowTop || !isTooltipIntersectingWindowBottom) {
          setTooltipHorizontalOffset({
            left: getPositionValue(alignLeft)
          });
        } else {
          setFullWidthTooltip();
        }
      };

      var handlePositionMiddleHorizontal = function handlePositionMiddleHorizontal() {
        if (!isTooltipIntersectingWindowLeft && !isTooltipIntersectingWindowRight) {
          setTooltipHorizontalOffset({
            left: getPositionValue(alignMiddleHorizonal)
          });
        } else if (isTooltipIntersectingWindowLeft && !isTooltipIntersectingWindowRight) {
          setTooltipHorizontalOffset({
            left: getPositionValue(alignLeft)
          });
        } else if (isTooltipIntersectingWindowRight && !isTooltipIntersectingWindowLeft) {
          setTooltipHorizontalOffset({
            right: getPositionValue(alignRight)
          });
        } else {
          setFullWidthTooltip();
        }
      };

      var handlePositionMiddleVerticalRightHorizontal = function handlePositionMiddleVerticalRightHorizontal() {
        if (isTooltipFitableBetweenComponentAndWindowRight && !isMiddleVerticalTooltipIntersectingWindowTop && !isMiddleVerticalTooltipIntersectingWindowBottom) {
          setTooltipHorizontalOffset({
            left: getPositionValue(alignMiddleVerticalRightHorizontal)
          });
        } else if (isTooltipFitableBetweenComponentAndWindowLeft && !isMiddleVerticalTooltipIntersectingWindowTop && !isMiddleVerticalTooltipIntersectingWindowBottom) {
          setTooltipHorizontalOffset({
            left: getPositionValue(alignMiddleVerticalLeftHorizontal)
          });
        } else if (!isTooltipIntersectingWindowRight) {
          setTooltipHorizontalOffset({
            left: getPositionValue(alignLeft)
          });
        } else if (!isTooltipIntersectingWindowTop || !isTooltipIntersectingWindowBottom) {
          setTooltipHorizontalOffset({
            right: getPositionValue(alignRight)
          });
        } else {
          setFullWidthTooltip();
        }
      }; // Vertical alignment handling


      switch (verticalAlignment) {
        case TOOLTIP_VERTICAL_ALIGNMENTS.BOTTOM:
          handlePositionBottom();
          break;

        case TOOLTIP_VERTICAL_ALIGNMENTS.MIDDLE:
          handlePositionMiddle();
          break;

        default:
          handlePositionTop();
      } // Horizontal alignment handling


      switch (horizontalAlignment) {
        case TOOLTIP_HORIZONTAL_ALIGNMENTS.MIDDLE:
          handlePositionMiddleHorizontal();
          break;

        case TOOLTIP_HORIZONTAL_ALIGNMENTS.RIGHT:
          if (isInitialVerticalPositionMiddle) {
            handlePositionMiddleVerticalRightHorizontal();
          } else {
            handlePositionRight();
          }

          break;

        default:
          if (isInitialVerticalPositionMiddle) {
            handlePositionMiddleVerticalLeftHorizontal();
          } else {
            handlePositionLeft();
          }

      }
    }, [documentHeight, documentWidth, getPositionValue, horizontalAlignment, isInitialHorizontalPositionMiddle, isInitialVerticalPositionMiddle, marginAroundElement, setFullWidthTooltip, verticalAlignment, windowScrollOffset]);

    var handleOnMouseOver = function handleOnMouseOver() {
      if (showTooltip) setIsVisible(true);
    };

    var handleOnFocus = function handleOnFocus() {
      if (showTooltip) setIsVisible(true);
    };

    var handleOnMouseOut = function handleOnMouseOut() {
      if (showTooltip) setIsVisible(false);
    };

    var handleOnBlur = function handleOnBlur() {
      if (showTooltip) setIsVisible(false);
    };

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        ref: componentContainerRef,
        children: /*#__PURE__*/_jsx(Component, _objectSpread({
          "aria-describedby": id,
          onBlur: handleOnBlur,
          onFocus: handleOnFocus,
          onMouseOut: handleOnMouseOut,
          onMouseOver: handleOnMouseOver,
          ref: ref,
          setTooltipText: setTooltipText,
          showTooltip: setShowTooltip
        }, other))
      }), showTooltip && isVisible && /*#__PURE__*/_jsx(Portal, {
        id: "tooltip-portal-".concat(id),
        children: /*#__PURE__*/_jsx(Tooltip, _objectSpread(_objectSpread({}, tooltipProps), {}, {
          ref: tooltipRef,
          style: _objectSpread(_objectSpread(_objectSpread({}, tooltipStyling), tooltipHorizontalOffset), tooltipVerticalOffset),
          text: tooltipText
        }))
      })]
    });
  });
  WithTooltip.propTypes = tooltipPositioningProps;
  WithTooltip.defaultProps = tooltipPositioningDefaultProps;
  return WithTooltip;
};

export { withTooltip };